import { HStack, Link as ChakraLink, Tooltip } from '@chakra-ui/react';
import { useBrand } from '../branding/useBrand';

/**
 * Brand social-media icons. Each platform's link is read from a per-brand
 * Vite env var. Empty / unset → that icon isn't rendered, so the team
 * can launch with whatever subset of channels is actually active and add
 * more later without a code change.
 *
 * Env vars (set in `.env.production`, redeploy SPA):
 *   VITE_DLC_TWITTER_URL          | VITE_MIXGENIE_TWITTER_URL
 *   VITE_DLC_INSTAGRAM_URL        | VITE_MIXGENIE_INSTAGRAM_URL
 *   VITE_DLC_TIKTOK_URL           | VITE_MIXGENIE_TIKTOK_URL
 *   VITE_DLC_YOUTUBE_URL          | VITE_MIXGENIE_YOUTUBE_URL
 *   VITE_DLC_FACEBOOK_URL         | VITE_MIXGENIE_FACEBOOK_URL
 *
 * Brand selection runs through `useBrand()` which inspects the hostname —
 * dicelacancion.com / www.dicelacancion.com get the DLC handles, every
 * other host gets the MixGenie set.
 */

type Platform = {
  key: string;
  label: string;
  envDlc: string | undefined;
  envMix: string | undefined;
  /** Tailored brand color for hover affordance. */
  color: string;
  icon: JSX.Element;
};

const PLATFORMS: Platform[] = [
  {
    key: 'twitter',
    label: 'X',
    envDlc: import.meta.env.VITE_DLC_TWITTER_URL,
    envMix: import.meta.env.VITE_MIXGENIE_TWITTER_URL,
    color: '#ffffff',
    icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
        <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
      </svg>
    ),
  },
  {
    key: 'instagram',
    label: 'Instagram',
    envDlc: import.meta.env.VITE_DLC_INSTAGRAM_URL,
    envMix: import.meta.env.VITE_MIXGENIE_INSTAGRAM_URL,
    color: '#E1306C',
    icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
        <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z" />
      </svg>
    ),
  },
  {
    key: 'tiktok',
    label: 'TikTok',
    envDlc: import.meta.env.VITE_DLC_TIKTOK_URL,
    envMix: import.meta.env.VITE_MIXGENIE_TIKTOK_URL,
    color: '#FF0050',
    icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
        <path d="M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-5.2 1.74 2.89 2.89 0 012.31-4.64 2.93 2.93 0 01.88.13V9.4a6.84 6.84 0 00-1-.05A6.33 6.33 0 005.8 20.1a6.34 6.34 0 0010.86-4.43V8.93a8.16 8.16 0 002.93.55V5.69z" />
      </svg>
    ),
  },
  {
    key: 'youtube',
    label: 'YouTube',
    envDlc: import.meta.env.VITE_DLC_YOUTUBE_URL,
    envMix: import.meta.env.VITE_MIXGENIE_YOUTUBE_URL,
    color: '#FF0000',
    icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
        <path d="M23.498 6.186a3.016 3.016 0 00-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 00.502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 002.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 002.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z" />
      </svg>
    ),
  },
  {
    key: 'facebook',
    label: 'Facebook',
    envDlc: import.meta.env.VITE_DLC_FACEBOOK_URL,
    envMix: import.meta.env.VITE_MIXGENIE_FACEBOOK_URL,
    color: '#1877F2',
    icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
        <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
      </svg>
    ),
  },
];

function pickUrl(p: Platform, isDlc: boolean): string | undefined {
  const raw = (isDlc ? p.envDlc : p.envMix) as string | undefined;
  if (typeof raw !== 'string') return undefined;
  const t = raw.trim();
  return t.length > 0 ? t : undefined;
}

export function SocialLinks() {
  const brand = useBrand();
  const links = PLATFORMS
    .map((p) => ({ ...p, url: pickUrl(p, brand.isDiceLaCancion) }))
    .filter((p) => !!p.url);

  if (links.length === 0) return null;

  return (
    <HStack spacing={2}>
      {links.map((p) => (
        <Tooltip key={p.key} label={p.label} placement="top" hasArrow>
          <ChakraLink
            href={p.url}
            isExternal
            rel="noopener noreferrer"
            aria-label={p.label}
            // Auto-captured by the Growth tracker as a cta_click. `social:*`
            // namespace mirrors the `streaming:*` convention used on the
            // song-page external links, so CTA Analytics groups them cleanly.
            data-cta={`social:${p.key}`}
            display="inline-flex"
            alignItems="center"
            justifyContent="center"
            boxSize="34px"
            borderRadius="full"
            bg="whiteAlpha.100"
            color="whiteAlpha.900"
            transition="all 120ms"
            _hover={{ bg: 'whiteAlpha.300', color: p.color }}
          >
            {p.icon}
          </ChakraLink>
        </Tooltip>
      ))}
    </HStack>
  );
}
